import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutlined';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import DownloadIcon from '@mui/icons-material/Download';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useTranslation } from '../common/components/LocalizationProvider';
import PageLayout from '../common/components/PageLayout';
import ReportsMenu from './components/ReportsMenu';
import { sessionActions } from '../store';
import useReportStyles from './common/useReportStyles';

const extractSessionId = (value) => {
  const text = String(value || '');
  const match = text.match(/\[([^\]:\s]+)(?::[^\]]*)?\]/);
  return match?.[1] || '';
};

const containsIdentifier = (item, identifier) => {
  const normalizedIdentifier = identifier.toLowerCase();
  return String(item.uniqueId || '').toLowerCase().includes(normalizedIdentifier)
    || String(item.data || '').toLowerCase().includes(normalizedIdentifier);
};

const LogsPage = () => {
  const { classes } = useReportStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const t = useTranslation();

  const [identifierFilter, setIdentifierFilter] = useState('');
  const [protocolFilter, setProtocolFilter] = useState('all');
  const [containsFilter, setContainsFilter] = useState('');
  const [onlyUnknownDevices, setOnlyUnknownDevices] = useState(false);
  const [downloadFormat, setDownloadFormat] = useState('json');
  const [isCollecting, setIsCollecting] = useState(false);

  useEffect(() => {
    dispatch(sessionActions.enableLogs(isCollecting));
  }, [dispatch, isCollecting]);

  useEffect(() => () => {
    dispatch(sessionActions.enableLogs(false));
  }, [dispatch]);


  useEffect(() => {
    dispatch(sessionActions.setLogCollectionIdentifier(identifierFilter));
  }, [dispatch, identifierFilter]);

  const items = useSelector((state) => state.session.logs);

  const protocolOptions = useMemo(() => ['all', ...Array.from(new Set(items.map((item) => item.protocol).filter(Boolean))).sort()], [items]);

  const filteredItems = useMemo(() => {
    const identifier = identifierFilter.trim().toLowerCase();
    const contains = containsFilter.trim().toLowerCase();

    const linkedSessions = new Set();

    if (identifier) {
      items.forEach((item) => {
        if (containsIdentifier(item, identifier)) {
          const sessionId = extractSessionId(item.data);
          if (sessionId) {
            linkedSessions.add(sessionId);
          }
        }
      });
    }

    return items.filter((item) => {
      if (identifier) {
        const hasIdentifier = containsIdentifier(item, identifier);
        const sessionId = extractSessionId(item.data);
        const linkedBySession = sessionId && linkedSessions.has(sessionId);

        if (!hasIdentifier && !linkedBySession) {
          return false;
        }
      }
      if (protocolFilter !== 'all' && item.protocol !== protocolFilter) {
        return false;
      }
      if (contains && !String(item.data || '').toLowerCase().includes(contains)) {
        return false;
      }
      if (onlyUnknownDevices && item.deviceId) {
        return false;
      }
      return true;
    });
  }, [items, identifierFilter, protocolFilter, containsFilter, onlyUnknownDevices]);

  const registerDevice = (uniqueId) => {
    const query = new URLSearchParams({ uniqueId });
    navigate(`/settings/device?${query.toString()}`);
  };

  const clearFilters = () => {
    setIdentifierFilter('');
    setProtocolFilter('all');
    setContainsFilter('');
    setOnlyUnknownDevices(false);
  };

  const downloadCollectedLogs = () => {
    const date = new Date().toISOString().replace(/[:.]/g, '-');
    const rows = filteredItems.map((item) => ({
      uniqueId: item.uniqueId || '',
      protocol: item.protocol || '',
      deviceId: item.deviceId || '',
      data: item.data || '',
    }));

    let blob;
    let extension;

    if (downloadFormat === 'csv') {
      const header = ['uniqueId', 'protocol', 'deviceId', 'data'];
      const csv = [header.join(','), ...rows.map((row) => header
        .map((field) => `"${String(row[field]).replaceAll('"', '""')}"`)
        .join(','))].join('\n');
      blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      extension = 'csv';
    } else if (downloadFormat === 'txt') {
      const txt = rows
        .map((row, index) => `#${index + 1}\nID: ${row.uniqueId}\nProtocolo: ${row.protocol}\nDispositivo: ${row.deviceId || 'não registrado'}\nData: ${row.data}\n`)
        .join('\n');
      blob = new Blob([txt], { type: 'text/plain;charset=utf-8' });
      extension = 'txt';
    } else {
      blob = new Blob([JSON.stringify(rows, null, 2)], { type: 'application/json;charset=utf-8' });
      extension = 'json';
    }

    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `logs-coletados-${date}.${extension}`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <PageLayout menu={<ReportsMenu />} breadcrumbs={['reportTitle', 'sharedLogs']}>
      <Stack spacing={1.5}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
            <TextField
              label="Identificador"
              value={identifierFilter}
              onChange={(event) => setIdentifierFilter(event.target.value)}
              size="small"
              fullWidth
            />
            <TextField
              select
              label="Protocolo"
              value={protocolFilter}
              onChange={(event) => setProtocolFilter(event.target.value)}
              size="small"
              sx={{ minWidth: { xs: '100%', md: 200 } }}
            >
              {protocolOptions.map((option) => (
                <MenuItem key={option} value={option}>{option === 'all' ? 'Todos' : option}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Contém no payload"
              value={containsFilter}
              onChange={(event) => setContainsFilter(event.target.value)}
              size="small"
              fullWidth
            />
            <Button
              variant={onlyUnknownDevices ? 'contained' : 'outlined'}
              onClick={() => setOnlyUnknownDevices((prev) => !prev)}
            >
              Somente não registrados
            </Button>
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} mt={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
            <TextField
              select
              label="Formato de download"
              value={downloadFormat}
              onChange={(event) => setDownloadFormat(event.target.value)}
              size="small"
              sx={{ minWidth: { xs: '100%', md: 220 } }}
            >
              <MenuItem value="json">JSON</MenuItem>
              <MenuItem value="csv">CSV</MenuItem>
              <MenuItem value="txt">TXT</MenuItem>
            </TextField>
            <Button
              startIcon={isCollecting ? <PauseIcon /> : <PlayArrowIcon />}
              variant={isCollecting ? 'contained' : 'outlined'}
              color={isCollecting ? 'warning' : 'primary'}
              onClick={() => setIsCollecting((prev) => !prev)}
            >
              {isCollecting ? 'Pausar coleta' : 'Iniciar coleta'}
            </Button>
            <Button startIcon={<DownloadIcon />} variant="contained" onClick={downloadCollectedLogs} disabled={!filteredItems.length}>
              Download logs filtrados
            </Button>
            <Button variant="outlined" onClick={clearFilters}>Limpar filtros</Button>
            <Button
              color="error"
              variant="outlined"
              startIcon={<DeleteSweepIcon />}
              onClick={() => dispatch(sessionActions.clearLogs())}
              disabled={!items.length}
            >
              Limpar logs coletados
            </Button>
          </Stack>

          <Box mt={1.5} display="flex" gap={1} flexWrap="wrap">
            <Chip label={`Coletados: ${items.length}`} color="primary" variant="outlined" />
            <Chip label={`Exibidos: ${filteredItems.length}`} color="success" variant="outlined" />
            <Chip label={`Não registrados: ${items.filter((item) => !item.deviceId).length}`} color="warning" variant="outlined" />
            <Chip label={isCollecting ? 'Coleta ativa' : 'Coleta pausada'} color={isCollecting ? 'success' : 'default'} variant="outlined" />
          </Box>
        </Paper>

        {!items.length && (
          <Alert severity="info">
            {isCollecting ? 'Aguardando coleta de logs em tempo real...' : 'Coleta pausada. Clique em "Iniciar coleta" para começar.'}
          </Alert>
        )}

        <div className={classes.tableCard}>
          <div className={classes.tableScroller}>
            <Table className={classes.table}>
              <TableHead>
                <TableRow>
                  <TableCell className={classes.columnAction} />
                  <TableCell>{t('deviceIdentifier')}</TableCell>
                  <TableCell>{t('positionProtocol')}</TableCell>
                  <TableCell>{t('commandData')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredItems.map((item, index) => (
                  <TableRow key={`${item.uniqueId}-${index}`}>
                    <TableCell className={classes.columnAction} padding="none">
                      {item.deviceId ? (
                        <IconButton color="success" size="small" disabled>
                          <CheckCircleOutlineIcon fontSize="small" />
                        </IconButton>
                      ) : (
                        <Tooltip title={t('loginRegister')}>
                          <IconButton color="error" size="small" onClick={() => registerDevice(item.uniqueId)}>
                            <HelpOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>{item.uniqueId}</TableCell>
                    <TableCell>{item.protocol}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                        {item.data}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </Stack>
    </PageLayout>
  );
};

export default LogsPage;
